import React, { useCallback, useEffect, useRef, useState } from 'react'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import ExplainSessionReview from './ExplainSessionReview'
import {
  EXPLAIN_SESSION_PAGE_SIZE,
  explainSessionQuestionText,
  explainStatusLabel,
  fetchExplainChat,
  fetchExplainDiagnosis,
  fetchExplainSessions,
  fetchStruggleProfile,
  formatExplainDate,
  formatExplainTimestamp
} from '../../helpers/explainSessions'
import './ParentExplainPanel.css'

const LOAD_ERROR =
  'We could not load these sessions right now. Please try again.'
const CHAT_ERROR =
  'We could not load this conversation right now. Please try again.'

/**
 * Read-only window onto a linked student's "Explain this math problem" work:
 * their sessions, the questions they asked in each one, the diagnosis a
 * session produced, and the rolling struggle summary.
 *
 * Reads are deliberately staged. The dashboard mount costs one bounded session
 * query plus one struggle-profile get; the chat thread and the diagnosis for a
 * session are fetched only when that session is opened. Nothing here
 * subscribes, so a parent leaving the tab open costs nothing.
 */
const ParentExplainPanel = ({ studentId, studentName }) => {
  const [sessions, setSessions] = useState([])
  const [cursor, setCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const [profile, setProfile] = useState(null)
  const [profileLoaded, setProfileLoaded] = useState(false)

  const [selectedSessionId, setSelectedSessionId] = useState(null)
  const [chat, setChat] = useState([])
  const [chatState, setChatState] = useState('idle')
  const [diagnosis, setDiagnosis] = useState(null)

  const isMountedRef = useRef(true)
  // Guards against a slow response for a previously selected session landing
  // after the parent has already opened a different one.
  const detailRequestRef = useRef(0)

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const loadFirstPage = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [page, struggleProfile] = await Promise.all([
        fetchExplainSessions(studentId),
        fetchStruggleProfile(studentId)
      ])
      if (!isMountedRef.current) return
      setSessions(page.sessions)
      setCursor(page.cursor)
      setHasMore(page.hasMore)
      setSelectedSessionId(null)
      setChat([])
      setDiagnosis(null)
      setChatState('idle')
      setProfile(struggleProfile)
      setProfileLoaded(true)
    } catch (error) {
      console.error('Error loading explain sessions for parent:', error)
      if (!isMountedRef.current) return
      setLoadError(LOAD_ERROR)
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [studentId])

  useEffect(() => {
    loadFirstPage()
  }, [loadFirstPage])

  const handleLoadMore = async () => {
    if (loadingMore || !cursor) return
    setLoadingMore(true)
    setLoadError(null)
    try {
      const page = await fetchExplainSessions(studentId, { cursor })
      if (!isMountedRef.current) return
      // Keyed by id so a session written between pages cannot be listed twice.
      setSessions(previous => {
        const seen = new Set(previous.map(session => session.id))
        return [...previous, ...page.sessions.filter(session => !seen.has(session.id))]
      })
      setCursor(page.cursor)
      setHasMore(page.hasMore)
    } catch (error) {
      console.error('Error loading more explain sessions:', error)
      if (!isMountedRef.current) return
      setLoadError(LOAD_ERROR)
    } finally {
      if (isMountedRef.current) setLoadingMore(false)
    }
  }

  const loadSessionDetail = useCallback(async (session) => {
    const requestId = detailRequestRef.current + 1
    detailRequestRef.current = requestId

    setChat([])
    setDiagnosis(null)
    // A session with no chat messages needs no query at all.
    setChatState(session.chatMessageCount > 0 ? 'loading' : 'loaded')

    try {
      const [messages, entry] = await Promise.all([
        session.chatMessageCount > 0
          ? fetchExplainChat(studentId, session.id)
          : Promise.resolve([]),
        fetchExplainDiagnosis(studentId, session)
      ])
      if (!isMountedRef.current || detailRequestRef.current !== requestId) return
      setChat(messages)
      setDiagnosis(entry)
      setChatState('loaded')
    } catch (error) {
      console.error('Error loading explain session detail:', error)
      if (!isMountedRef.current || detailRequestRef.current !== requestId) return
      setChatState('error')
    }
  }, [studentId])

  const handleSelectSession = (session) => {
    setSelectedSessionId(session.id)
    loadSessionDetail(session)
  }

  const selectedSession = sessions.find(session => session.id === selectedSessionId) || null

  const handleRetryChat = () => {
    if (selectedSession) loadSessionDetail(selectedSession)
  }

  const renderStruggleProfile = () => {
    if (!profileLoaded) return null
    return (
      <div className="parent-explain-profile">
        <div className="parent-desktop-detail-label">Struggle summary</div>
        {profile ? (
          <>
            <p className="parent-explain-profile-summary">{profile.summary}</p>
            {profile.updatedAt && (
              <div className="parent-explain-profile-meta">
                Updated {formatExplainTimestamp(profile.updatedAt)}
              </div>
            )}
          </>
        ) : (
          <p className="parent-explain-profile-empty">
            {studentName} does not have a struggle summary yet. It is written
            automatically once they have finished a few explain sessions.
          </p>
        )}
      </div>
    )
  }

  if (loading) {
    return <div className="parent-desktop-loading">Loading explain sessions...</div>
  }

  if (loadError && sessions.length === 0) {
    return (
      <div className="parent-desktop-empty-questions">
        <div className="parent-explain-load-error">
          <p>{loadError}</p>
          <button
            type="button"
            className="parent-explain-retry-btn"
            onClick={loadFirstPage}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`parent-desktop-view-work-layout ${selectedSession ? 'question-selected' : ''}`}>
      <div className="parent-desktop-questions-menu">
        <div className="parent-desktop-questions-menu-header">
          <div className="parent-desktop-questions-title">Explain sessions</div>
        </div>
        <div className="parent-desktop-questions-list-scroll">
          {sessions.length === 0 ? (
            <div className="parent-explain-empty-list">
              <p>{studentName} hasn't used "Explain this math problem" yet.</p>
            </div>
          ) : (
            <div className="parent-desktop-date-section">
              {sessions.map(session => (
                <div
                  key={session.id}
                  className={`parent-desktop-question-item ${selectedSessionId === session.id ? 'selected' : ''}`}
                  onClick={() => handleSelectSession(session)}
                >
                  <div className="parent-explain-item-tags">
                    <span className={`parent-explain-status-chip ${session.status}`}>
                      {explainStatusLabel(session.status)}
                    </span>
                    {session.chatMessageCount > 0 && (
                      <span className="parent-explain-item-flag">Asked questions</span>
                    )}
                  </div>
                  <div className="parent-desktop-question-preview">
                    <MixedContentPreview
                      content={explainSessionQuestionText(session)}
                      fontSize="14px"
                    />
                  </div>
                  <div className="parent-desktop-question-source-label">
                    {formatExplainDate(session.createdAt) || 'Date unavailable'}
                    {session.topic ? ` · ${session.topic}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <button
              type="button"
              className="parent-explain-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : `Load ${EXPLAIN_SESSION_PAGE_SIZE} more`}
            </button>
          )}

          {loadError && sessions.length > 0 && (
            <div className="parent-explain-inline-error" role="alert">{loadError}</div>
          )}
        </div>
      </div>

      <div className="parent-desktop-answers-viewer">
        {selectedSession ? (
          <div className="parent-explain-detail">
            {/* Always visible: on desktop this is the only way back to the
                struggle summary, which lives in the unselected state. */}
            <button
              type="button"
              className="parent-explain-back-btn"
              onClick={() => setSelectedSessionId(null)}
            >
              ← Back to sessions
            </button>
            <ExplainSessionReview
              session={selectedSession}
              showChat
              chat={chat}
              chatState={chatState}
              chatError={CHAT_ERROR}
              onRetryChat={handleRetryChat}
              diagnosis={diagnosis}
            />
          </div>
        ) : (
          <div className="parent-explain-overview">
            {renderStruggleProfile()}
            <p className="parent-desktop-placeholder-text">
              {sessions.length === 0
                ? 'Explain sessions will show up here once they start one.'
                : `Select a session to see the steps ${studentName} worked through.`}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ParentExplainPanel
